type TFunction = (key: string, options?: any) => string;

export type AuditSubjectConfig = {
  /** i18n key, with fallback to humanized subjectType */
  labelKey: string;
  routeBuilder?: (companyId: string, subjectId: string) => string | null;
};

const registry: Record<string, AuditSubjectConfig> = {
  Customer: {
    labelKey: "audit_subject_Customer",
    routeBuilder: (companyId, id) => `/app/${companyId}/customers/${id}`,
  },
  CustomerFinance: {
    labelKey: "audit_subject_CustomerFinance",
  },
  File: {
    labelKey: "audit_subject_File",
    routeBuilder: (companyId, id) => `/app/${companyId}/documents/view/${id}`,
  },
  Asset: {
    labelKey: "audit_subject_Asset",
    routeBuilder: (companyId, id) => `/app/${companyId}/assets/${id}`,
  },
  FuelReport: {
    labelKey: "audit_subject_FuelReport",
    routeBuilder: (companyId, id) => `/app/${companyId}/fuel-reports/${id}`,
  },
  FuelBarcode: {
    labelKey: "audit_subject_FuelBarcode",
    routeBuilder: (companyId) =>
      `/app/${companyId}/settings/fuel?subTab=barcodes`,
  },
  BusinessTrip: {
    labelKey: "audit_subject_BusinessTrip",
    routeBuilder: (companyId, id) => `/app/${companyId}/business-trips/${id}`,
  },
  Order: {
    labelKey: "audit_subject_Order",
    routeBuilder: (companyId, id) => `/app/${companyId}/orders/${id}`,
  },
  Project: {
    labelKey: "audit_subject_Project",
  },
  ProjectBoard: {
    labelKey: "audit_subject_ProjectBoard",
    routeBuilder: (companyId, id) => `/app/${companyId}/project-boards/${id}`,
  },
  ProjectBoardColumn: {
    labelKey: "audit_subject_ProjectBoardColumn",
    routeBuilder: (companyId) => `/app/${companyId}/project-boards`,
  },
  User: {
    labelKey: "audit_subject_User",
    routeBuilder: (companyId, id) => `/app/${companyId}/users/${id}`,
  },
  UserMembership: {
    labelKey: "audit_subject_UserMembership",
    routeBuilder: (companyId) => `/app/${companyId}/users`,
  },
  Department: {
    labelKey: "audit_subject_Department",
    routeBuilder: (companyId, id) => `/app/${companyId}/departments/${id}`,
  },
  Onboarding: {
    labelKey: "audit_subject_Onboarding",
    routeBuilder: (companyId, id) => `/app/${companyId}/onboarding/${id}`,
  },
  Company: {
    labelKey: "audit_subject_Company",
    routeBuilder: (companyId) => `/app/${companyId}/settings/company`,
  },
  CompanyHoliday: {
    labelKey: "audit_subject_CompanyHoliday",
    routeBuilder: (companyId) => `/app/${companyId}/settings/holidays`,
  },
  CompaniesSignOnTab: {
    labelKey: "audit_subject_CompaniesSignOnTab",
    routeBuilder: (companyId) => `/app/${companyId}/settings/signing`,
  },
  PermissionsGroup: {
    labelKey: "audit_subject_PermissionsGroup",
    routeBuilder: (companyId) => `/app/${companyId}/settings/permissions`,
  },
  CustomField: {
    labelKey: "audit_subject_CustomField",
    routeBuilder: (companyId) => `/app/${companyId}/customfields`,
  },
  Series: {
    labelKey: "audit_subject_Series",
    routeBuilder: (companyId) => `/app/${companyId}/series`,
  },
  FileType: {
    labelKey: "audit_subject_FileType",
    routeBuilder: (companyId) => `/app/${companyId}/filestypes`,
  },
  FileCategory: {
    labelKey: "audit_subject_FileCategory",
  },
  AssetType: {
    labelKey: "audit_subject_AssetType",
    routeBuilder: (companyId) => `/app/${companyId}/assetstypes`,
  },
  Template: {
    labelKey: "audit_subject_Template",
    routeBuilder: (companyId) => `/app/${companyId}/files/templates`,
  },
  SignontabUser: {
    labelKey: "audit_subject_SignontabUser",
    routeBuilder: (companyId) => `/app/${companyId}/signontab-users`,
  },
  Expense: {
    labelKey: "audit_subject_Expense",
  },
  TripExpense: {
    labelKey: "audit_subject_TripExpense",
  },
  PointTransaction: {
    labelKey: "audit_subject_PointTransaction",
    routeBuilder: (companyId) => `/app/${companyId}/recognition`,
  },
};

/** Ordered subject types shown in the Activity log filter dropdown. */
export const AUDIT_SUBJECT_FILTER_TYPES = [
  "Company",
  "CompanyHoliday",
  "CompaniesSignOnTab",
  "User",
  "UserMembership",
  "PermissionsGroup",
  "CustomField",
  "Series",
  "FileType",
  "FileCategory",
  "Template",
  "AssetType",
  "SignontabUser",
  "FuelBarcode",
  "Department",
  "Customer",
  "CustomerFinance",
  "File",
  "Asset",
  "Order",
  "BusinessTrip",
  "TripExpense",
  "Expense",
  "FuelReport",
  "Onboarding",
  "Project",
  "ProjectBoard",
  "ProjectBoardColumn",
  "PointTransaction",
] as const;

export function getAuditSubjectConfig(subjectType: string): AuditSubjectConfig {
  return (
    registry[subjectType] || {
      labelKey: `audit_subject__${subjectType}`,
    }
  );
}

export function getSubjectTypeLabel(
  t: TFunction,
  subjectType: string
): string {
  const c = getAuditSubjectConfig(subjectType);
  return t(c.labelKey, { defaultValue: subjectType });
}

export function getSubjectEntityHref(
  subjectType: string,
  companyId: string,
  subjectId: string | null | undefined
): string | null {
  if (!companyId || !subjectId) {
    return null;
  }
  const c = registry[subjectType];
  if (c && typeof c.routeBuilder === "function") {
    return c.routeBuilder(companyId, String(subjectId));
  }
  return null;
}
